/**
 * ProgressTrack — una barra de avance con su rótulo y su porcentaje.
 *
 * ⚠️ El valor se recorta a [0, 100] antes de pintar: con datos reales un avance
 * puede venir en 103% —más folios usados que autorizados, más horas cargadas
 * que presupuestadas— y sin el recorte la barra se sale del carril. Lo que se
 * muestra está topado; el número de verdad va en `hint`, que es donde un exceso
 * se puede explicar.
 *
 * ⚠️ `role="progressbar"` con sus tres valores: sin eso la barra es un `<div>`
 * de colores y el lector de pantalla no anuncia ningún avance.
 */
import type { HTMLAttributes, ReactNode } from 'react'

export interface ProgressTrackProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
  label?: ReactNode
  value?: number
  max?: number
  /** Debajo de la barra: el detalle que el porcentaje no cuenta. */
  hint?: ReactNode
  showValue?: boolean
  className?: string
}

export function ProgressTrack({
  label,
  value = 0,
  max = 100,
  hint,
  showValue = true,
  className = '',
  ...rest
}: ProgressTrackProps) {
  const pct = Math.max(0, Math.min(100, (value / (max || 1)) * 100))

  return (
    <div className={className} {...rest}>
      {(label || showValue) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 12,
            marginBottom: 6,
          }}
        >
          {label && <span style={{ color: 'rgb(var(--color-content-muted))' }}>{label}</span>}
          {showValue && (
            <span className="cl-mono" style={{ color: 'rgb(var(--color-content-subtle))' }}>
              {Math.round(pct)}%
            </span>
          )}
        </div>
      )}
      <div
        className="cl-track"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={typeof label === 'string' ? label : undefined}
      >
        <div className="cl-track__fill" style={{ width: `${pct}%` }} />
      </div>
      {hint && (
        <div className="cl-hint" style={{ marginTop: 7 }}>
          {hint}
        </div>
      )}
    </div>
  )
}
